import type { HTMLAttributes } from 'react';
import { cn } from '../lib/cn';

/**
 * Skeleton loading placeholder.
 *
 * Renders a shimmer sweep (CSS pseudo-element, no JS) over an inset
 * background block. The shimmer is gated on `prefers-reduced-motion:
 * no-preference` — users who prefer reduced motion get a static block
 * with zero animation overhead instead.
 *
 * Performance: pure CSS animation, no timers, no JS, no network.
 * The shimmer reads two CSS custom properties (`--bg-inset` and
 * `--bg-elevated`) already present in every rendered page.
 */
export function Skeleton({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        // Base shape: inset surface, medium corner radius, clip overflow
        // so the shimmer gradient doesn't bleed outside the element.
        'overflow-hidden rounded-md bg-bg-inset',
        // Shimmer via ::before — only when the user allows motion.
        // In prefers-reduced-motion:reduce these classes are never applied,
        // leaving a perfectly static placeholder block with zero CPU cost.
        'motion-safe:relative',
        'motion-safe:before:absolute motion-safe:before:inset-0',
        'motion-safe:before:-translate-x-full motion-safe:before:animate-shimmer',
        'motion-safe:before:bg-gradient-to-r motion-safe:before:from-transparent',
        'motion-safe:before:via-bg-elevated motion-safe:before:to-transparent',
        className,
      )}
      {...props}
    />
  );
}
